import * as SelectPrimitive from "@radix-ui/react-select";
import { ComponentProps, useMemo, useState } from "react";
import t from "@/lang/lang";
import { useDebounce } from "@/shared/lib/debounce/debounce";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { NullUUID } from "@/shared/lib/utilities/uuid";

export type CustomerSelectOption = {
  id: string;
  name: string;
  addressLine?: string;
};

type Props = ComponentProps<typeof SelectPrimitive.Root> & {
  customerOptions: CustomerSelectOption[];
  showUnsetOption?: boolean;
  hideAddress?: boolean;
};

export function CustomerSelectFrom({
  value,
  customerOptions,
  showUnsetOption = false,
  hideAddress = false,
  ...props
}: Props) {
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search.trim().toLowerCase(), 250);

  const filteredCustomers = useMemo(() => {
    const base = [...customerOptions].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );

    if (!debouncedSearch) {
      return base;
    }

    return base.filter((customer) => {
      const searchableAddress = customer.addressLine?.toLowerCase() ?? "";
      return (
        customer.name.toLowerCase().includes(debouncedSearch) ||
        searchableAddress.includes(debouncedSearch)
      );
    });
  }, [customerOptions, debouncedSearch]);

  const showNoCustomersFound =
    customerOptions.length === 0 || filteredCustomers.length === 0;

  return (
    <Select value={value} {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t("Kunde auswählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Suche...")}
            className="border-0"
          />
        </SelectGroup>
        <SelectSeparator />
        {showUnsetOption && (
          <>
            <SelectGroup>
              <SelectItem value={NullUUID}>
                <span>{t("Kein Kunde")}</span>
              </SelectItem>
            </SelectGroup>
            <SelectSeparator />
          </>
        )}
        <SelectGroup>
          {filteredCustomers.map((customer) => (
            <SelectItem value={customer.id} key={customer.id}>
              <span>{customer.name}</span>
              {!hideAddress && customer.addressLine && (
                <span className="text-muted-foreground text-xs">
                  {customer.addressLine}
                </span>
              )}
            </SelectItem>
          ))}
          {showNoCustomersFound && (
            <SelectItem value="not-found" disabled>
              <span>{t("Keine Kunden gefunden")}</span>
            </SelectItem>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
